import type { IConfig } from '../domain/types'
import type { ActivePromotion, IPromotion, PromotionType } from '../domain/types/graphql'
import { setPromotionUiDetailsCatalog } from '../domain/promotions/setPromotionUiDetailsCatalog'
import { calculateItemPriceTotal } from '../domain/utilsForCartReducer'
import type { CartLineInput } from './cartRow'

/**
 * One line of the `cart.items` a cart query returns, narrowed to what a row
 * reads. `Item` — cinnamon's name for the full line — satisfies this.
 */
export interface CartItemInput {
  sku?: string | null
  /** What distinguishes two lines of the same product: units versus a case. */
  packageType?: string | null
  package?: { name?: string | null } | null
  /** Set on the lines a bundle produced, which are also same-sku siblings. */
  comboGroupId?: string | null
  name?: string | null
  imageURL?: string[] | null
  price?: number | null
  quantity?: number | null
  discounts?: number | null
  /**
   * Not selected by `cartFieldsQuery` today, so it is always absent from a
   * server response. It is carried because `calculateItemPriceTotal` reads it:
   * a line from a TOTAL promotion is priced by the promotion, not by the
   * product, and dropping the field here would silently change that rule.
   */
  promotion?: { type?: PromotionType; value?: number } | null
  promotions?: IPromotion[] | null
}

/**
 * A cart's line items, whichever shape the storefront returns them in.
 *
 * Most tenants answer with a flat `items` array. Some — `prueba` groups by
 * payment method (`groupBy: ["products", "CREDIT"]`) — return an **empty**
 * `items` and put the lines under `groups[].items` instead, same line shape,
 * one level deeper. Reading only `items` made those carts look empty while
 * `cart.total` was real: an add that silently vanished.
 *
 * This resolves both: `items` when it carries lines, otherwise every group's
 * items concatenated. Generic so it preserves the caller's own line type. The
 * grouping itself is dropped on purpose — the storefront's cart is one flat
 * list, and the payment-method split the groups encode is not part of that UI.
 */
export const cartLineItemsOf = <Line>(
  cart:
    | {
        items?: readonly Line[] | null
        groups?: readonly { items?: readonly Line[] | null }[] | null
      }
    | null
    | undefined,
): Line[] => {
  const items = cart?.items ?? []
  if (items.length > 0) return [...items]
  return (cart?.groups ?? []).flatMap((group) => [...(group.items ?? [])])
}

export interface ToCartLineInputsArgs {
  items: readonly CartItemInput[] | null | undefined
  /** Cart-level promotions. Also the only place freegoods are named. */
  activePromotions?: readonly ActivePromotion[] | null | undefined
  config?: IConfig | undefined
}

/**
 * The skus the cart is giving away.
 *
 * A freegood is not a flag on the line — the backend reports it under the
 * promotion that granted it, in `activePromotions[].freeGoods[].items[]`. That
 * is what makes a row unremovable: the shopper did not add it, so the shopper
 * cannot take it out.
 */
export const freegoodSkusOf = (
  activePromotions: readonly ActivePromotion[] | null | undefined,
): Set<string> => {
  const skus = new Set<string>()
  for (const promotion of activePromotions ?? []) {
    for (const group of promotion?.freeGoods ?? []) {
      for (const item of group?.items ?? []) {
        if (item?.sku) skus.add(item.sku)
      }
    }
  }
  return skus
}

/**
 * A line's identity, from what actually distinguishes it: the product, the
 * package it was bought in, and the bundle that produced it.
 *
 * A cart line carries no id of its own, and the sku alone is not unique — React
 * renders two children with the same key as one, and the stepper's draft state
 * would follow whichever survived. The occurrence suffix is the last resort for
 * two lines nothing else tells apart; an index alone would be worse, because
 * removing a line renumbers every line after it and the surviving rows would
 * inherit each other's state.
 */
const lineKeyFor = (item: CartItemInput, taken: Map<string, number>): string => {
  const base = [item.sku, item.packageType ?? item.package?.name ?? '', item.comboGroupId ?? '']
    .join('|')
  const seen = taken.get(base) ?? 0
  taken.set(base, seen + 1)
  return seen === 0 ? base : `${base}#${seen + 1}`
}

/**
 * The cart query result as the rows need it.
 *
 * Lines are not deduplicated, unlike the catalog: the same sku can legitimately
 * appear twice — bought as units and as a package — and merging them would show
 * the shopper one row they cannot map back to what they added.
 */
export const toCartLineInputs = ({
  items,
  activePromotions,
  config,
}: ToCartLineInputsArgs): CartLineInput[] => {
  const freegoods = freegoodSkusOf(activePromotions)
  const taken = new Map<string, number>()
  const lines = (items ?? []).filter((item): item is CartItemInput => Boolean(item?.sku))

  /**
   * The skus where a *paid* line can be told apart from the gift, because some
   * line of that sku costs nothing.
   *
   * A shopper can buy a product the cart is also giving away — claim the free
   * café, then add a café to the order. Marking every line of that sku as a
   * gift made the paid one unremovable, and nothing on screen said why.
   *
   * Narrow on purpose: only a sku that has a free line at all is split this way.
   * When every line of a granted sku costs something, the rule stays what it
   * was — all of them are gifts — because whether this backend prices a gift at
   * zero or prices it and discounts it elsewhere is **not verified against a
   * live storefront** (`createSession` on staging still 404s, see
   * `docs/NOT_PORTED.md`). Guessing there would risk the opposite bug: a gift
   * the shopper can remove, leaving the promotion holding nothing.
   */
  const splittableGiftSkus = new Set(
    lines
      .filter(
        (item) =>
          freegoods.has(item.sku as string) &&
          calculateItemPriceTotal({
            price: item.price ?? 0,
            quantity: item.quantity ?? 0,
            discounts: item.discounts ?? undefined,
            promotion: item.promotion ?? undefined,
          }) === 0,
      )
      .map((item) => item.sku as string),
  )

  return lines
    .map((item) => {
      const price = item.price ?? 0
      const quantity = item.quantity ?? 0
      const lineTotal = calculateItemPriceTotal({
        price,
        quantity,
        discounts: item.discounts ?? undefined,
        promotion: item.promotion ?? undefined,
      })

      return {
        lineKey: lineKeyFor(item, taken),
        sku: item.sku as string,
        name: item.name ?? '',
        image: item.imageURL?.[0] ?? '',
        unitPrice: price,
        quantity,
        lineTotal,
        /**
         * The sku alone is not enough. A shopper can buy a product the cart is
         * *also* giving away — claim the free café, then add a café to the
         * order — and marking every line of that sku as a gift made the paid one
         * unremovable: the shopper could not take out a line they had paid for,
         * and nothing on screen explained why.
         *
         * What separates them is what the line costs. A gift is free, so it
         * contributes nothing to the total; the paid line does. `lineTotal`
         * rather than `price` because a promotion can zero a line through
         * `discounts` instead of through the price.
         */
        // A granted sku is a gift unless this cart shows both shapes of it, in
        // which case the free line is the gift and the paid one is the
        // shopper's to remove. See `splittableGiftSkus`.
        isFreegood:
          freegoods.has(item.sku as string) &&
          (!splittableGiftSkus.has(item.sku as string) || lineTotal === 0),
        promotionUiDetails: setPromotionUiDetailsCatalog({
          product: { promotions: item.promotions, price, quantity },
          config,
          ...(activePromotions ? { activePromotions: [...activePromotions] } : {}),
        }),
      }
    })
}
